import { PatchCollection } from '@reduxjs/toolkit/dist/query/core/buildThunks';
import { omit } from 'lodash';
import { v4 } from 'uuid';

import { IconType } from 'src/components/icons';
import {
  AppAPIName,
  EmbedlyData,
  ExtensionConfig,
  ExtensionsSettings,
  ModuleSettingsItem,
  ModuleType,
  UnAuthAPIName,
  VisibilityRule,
} from 'src/constants';
import { MarketplaceApplication } from 'src/constants/dataTypes/appsTypes';
import { DEFAULT_APPS } from 'src/hooks/useApps';
import { insertModuleSettingsItem } from 'src/legacy/components/Settings/AppSetupPage/folderUtils';
import { RootState } from 'src/store/reduxTypes';
import {
  updateExtensionSettingsAction,
  updateModuleSettings,
} from 'src/store/settings/actions';
import { API } from 'src/utils/AmplifyApiUtils';
import { IsLoggedIn } from 'src/utils/AuthUtils';

import { ApiMethods, ApiTags, appAPI } from '.';

type AppInstallType =
  | 'core'
  | 'manual'
  | 'link'
  | 'embed'
  | 'custom'
  | 'marketplace'
  | 'folder';
export interface AppInstall {
  id: string;
  displayName: string;
  type: AppInstallType;
  appId: string;
  content: string;
  icon: string;
  sort: number;
  disabled: boolean;
  embedCount: number;
  isAuthenticated: boolean;
  autoSizeEmbed: boolean;
  embedlyData: EmbedlyData;
  embedCode: string;
  embedLink: string;
  visibilityConfig?: VisibilityRule[];
  queryParams?: Record<string, string>[];
  companyId?: string;
  path?: string;
  iuSidebarHidden: boolean;
  isInternalApp?: boolean;
}

export interface MarketplaceApplicationWebhook {
  eventName: string;
  eventDescription: string;
}

const appTypeMap: Record<AppInstallType, ModuleType> = {
  core: 'module',
  manual: 'local',
  link: 'global',
  embed: 'global',
  custom: 'global',
  marketplace: 'global',
  folder: 'folder',
};

const mapInstallsToModuleSettings = (
  installs: AppInstall[],
): ModuleSettingsItem[] => {
  const apps = installs.map((install) => {
    return {
      id: install.id,
      label: install.displayName,
      icon: install.icon,
      type: appTypeMap[install.type],
      disabled: install.disabled,
      visibilityConfig: install.visibilityConfig,
      path: install.path,
      iuSidebarHidden: install.iuSidebarHidden,
      isInternalApp: install.isInternalApp,
    };
  });
  // installs api doesn't return disabled modules so we should add the
  // default core modules as disabled here
  DEFAULT_APPS.forEach((module) => {
    if (!apps.find((i) => i.id === module.id)) {
      apps.push({
        id: module.id,
        label: module.title,
        icon: module.iconName,
        type: 'module',
        disabled: true,
        visibilityConfig: undefined,
        path: undefined,
        iuSidebarHidden: false,
        isInternalApp: false,
      });
    }
  });
  return apps;
};

/**
 * Query type discriminator for getInstalls endpoint.
 * Using a single string discriminator ensures consistent cache keys.
 * - undefined/void: default query (no folders, not preview)
 * - 'withFolders': includes folder structure
 * - 'withFoldersPreview': includes folders and previews as client
 */
export type GetInstallsQueryType = 'withFolders' | 'withFoldersPreview';

const mapInstallsToExtensionsSettings = (
  installs: AppInstall[],
): ExtensionsSettings => {
  const settings: Record<string, ExtensionConfig> = {};
  installs.forEach((install) => {
    const appType = appTypeMap[install.type];
    if (appType == 'global' || appType == 'local') {
      settings[install.id] = {
        type: appType,
        sort: install.sort ?? 0,
        icon: install.icon as IconType,
        name: install.displayName,
        embedCount: install.embedCount,
        isAuthenticated: install.isAuthenticated,
        appId: install.appId,
        embedCode: install.embedCode || install.content,
        embedLink: install.embedLink,
        autoSizeEmbed: install.autoSizeEmbed,
        embedlyData: install.embedlyData,
        queryParams: install.queryParams,
        companyId: install.companyId,
      };
    }
  });
  return settings;
};

export const applicationSettingsApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    createApp: build.mutation<
      MarketplaceApplication,
      Omit<MarketplaceApplication, 'id'> & {
        icon: IconType;
        includeFolders?: boolean;
        folderId?: string;
      }
    >({
      query: (body) => ({
        method: ApiMethods.post,
        options: { body: { ...body } },
        path: '/apps',
        body,
      }),
      async onQueryStarted(input, { dispatch, getState, queryFulfilled }) {
        // This is terrible. RTK offers no solution for typing getState
        // so we have to resort to typecasting.
        const state = getState() as unknown as RootState;
        const queryType: GetInstallsQueryType | undefined = input.includeFolders
          ? 'withFolders'
          : undefined;
        const installsData =
          applicationSettingsApi.endpoints.getInstalls.select(queryType)(state);
        const { moduleSettings, extensionsSettings } = installsData.data ?? {};

        const {
          data: { id: appId },
        } = await queryFulfilled;

        const newExtensionModuleConfigId = v4();

        // TODO(foley): what do we do about sort? do we even need it on the extension?
        const newExtensionModule: ExtensionConfig = {
          id: newExtensionModuleConfigId,
          type: 'global' as const,
          isAuthenticated: true,
          icon: input.icon,
          name: input.name,
          appId,
          autoSizeEmbed: true,
          sort: 0, // sort is not used any more. All extensionConfig objects has sort 0
        };

        const moduleSettingsItem: ModuleSettingsItem = {
          id: newExtensionModuleConfigId,
          label: input.name,
          icon: input.icon,
          disabled: false,
          type: 'global',
          visibilityConfig: input.visibilityConfig,
          iuSidebarHidden: false,
          isInternalApp: !input.clientUrl,
        };

        // when app is created, optimistically perform install side-effects
        // which includes updating the moduleSettings and extensionsSettings
        const patch = dispatch(
          applicationSettingsApi.util.updateQueryData(
            'getInstalls',
            queryType,
            (draftState) => {
              draftState.moduleSettings = insertModuleSettingsItem(
                draftState.moduleSettings,
                moduleSettingsItem,
                input.folderId,
              );
              draftState.extensionsSettings[newExtensionModuleConfigId] =
                newExtensionModule;
            },
          ),
        );
        let patch2: PatchCollection | undefined;
        if (input.includeFolders) {
          // if folders are included, also optimistically update the query cache
          // without folders
          patch2 = dispatch(
            applicationSettingsApi.util.updateQueryData(
              'getInstalls',
              undefined,
              (draftState) => {
                draftState.moduleSettings = insertModuleSettingsItem(
                  draftState.moduleSettings,
                  moduleSettingsItem,
                  input.folderId,
                ).filter((updatedModule) => updatedModule.type !== 'folder');
                draftState.extensionsSettings[newExtensionModuleConfigId] =
                  newExtensionModule;
              },
            ),
          );
        }

        try {
          // The order is important here. We need to update the extension settings
          // before the module settings, because the module settings depend on the
          // extension settings.
          await dispatch(
            updateExtensionSettingsAction({
              ...(extensionsSettings ?? {}),
              [newExtensionModuleConfigId]:
                newExtensionModule as ExtensionConfig,
            }),
          );

          await dispatch(
            updateModuleSettings(
              insertModuleSettingsItem(
                moduleSettings ?? [],
                moduleSettingsItem,
                input.folderId,
              ),
              {
                disableSuccessAlert: true,
                queryType,
              },
            ),
          );
        } catch (e) {
          await patch.undo();
          if (patch2) {
            await patch2.undo();
          }
        }
      },
      invalidatesTags: [ApiTags.apps],
    }),
    updateApp: build.mutation<
      MarketplaceApplication,
      MarketplaceApplication & {
        icon: IconType;
        moduleId: string;
        includeFolders?: boolean;
      }
    >({
      query: (body) => ({
        method: ApiMethods.put,
        options: {
          body: omit(body, 'id', 'icon', 'moduleId', 'includeFolders'),
        },
        path: `/apps/${body.id}`,
      }),
      async onQueryStarted(input, { dispatch, getState, queryFulfilled }) {
        // This is terrible. RTK offers no solution for typing getState
        // so we have to resort to typecasting.
        const state = getState() as unknown as RootState;
        const queryType: GetInstallsQueryType | undefined = input.includeFolders
          ? 'withFolders'
          : undefined;
        const installsData =
          applicationSettingsApi.endpoints.getInstalls.select(queryType)(state);
        const { moduleSettings, extensionsSettings } = installsData.data ?? {};
        if (!moduleSettings) {
          throw new Error('Module settings not found');
        }

        const moduleSettingsItem = (moduleSettings ?? []).find(
          (m) => m.id === input.moduleId,
        );
        if (!moduleSettings || !moduleSettingsItem || !input.moduleId) {
          // TODO(foley): Implement error handling.
          return;
        }

        const extensionSettingsItem = extensionsSettings?.[input.moduleId];
        if (!extensionSettingsItem) {
          // TODO(foley): Implement error handling.
          return;
        }

        await queryFulfilled;

        // The order is important here. We need to update the extension settings
        // before the module settings, because the module settings depend on the
        // extension settings.

        await dispatch(
          updateExtensionSettingsAction({
            ...(extensionsSettings ?? {}),
            [input.moduleId]: {
              ...extensionSettingsItem,
              name: input.name,
              // If icon ever gets set to '' it causes trouble, so let's
              // make sure that does not happen.
              icon: input.icon || 'dashboard',
            },
          }),
        );

        await dispatch(
          updateModuleSettings(
            moduleSettings.map((m) => {
              if (m.id === input.moduleId) {
                return {
                  ...m,
                  label: input.name,
                  icon: input.icon || 'dashboard',
                  isInternalApp: !input.clientUrl,
                };
              }
              return m;
            }),
            {
              disableSuccessAlert: true,
              queryType,
            },
          ),
        );
      },
      invalidatesTags: [ApiTags.apps],
    }),
    getApp: build.query<MarketplaceApplication, { id: string }>({
      query: ({ id }) => ({
        path: `/apps/${id}`,
        method: ApiMethods.get,
        options: {
          queryStringParameters: {},
        },
      }),
      providesTags: [ApiTags.apps],
    }),
    deleteApp: build.mutation<MarketplaceApplication, { id: string }>({
      query: ({ id }) => ({
        path: `/apps/${id}`,
        method: ApiMethods.del,
        options: {
          queryStringParameters: {},
        },
      }),
    }),
    getInstalls: build.query<
      {
        moduleSettings: ModuleSettingsItem[];
        extensionsSettings: ExtensionsSettings;
      },
      GetInstallsQueryType | void
    >({
      queryFn: async (queryType) => {
        const includeFolders =
          queryType === 'withFolders' || queryType === 'withFoldersPreview';
        const previewAsClient = queryType === 'withFoldersPreview';

        const queryParams = new URLSearchParams();
        if (includeFolders) queryParams.set('includeFolders', 'true');
        if (previewAsClient) queryParams.set('previewAsClient', 'true');
        const queryString = queryParams.toString();

        const isLoggedInUser = await IsLoggedIn();
        const installs = await API.get(
          isLoggedInUser ? AppAPIName : UnAuthAPIName,
          `${isLoggedInUser ? '' : '/public'}/v1/installs${queryString ? `?${queryString}` : ''}`,
          {},
        );
        // map installs into their respective moduleSettings and extensionsSettings
        return {
          data: {
            moduleSettings: mapInstallsToModuleSettings(installs),
            extensionsSettings: mapInstallsToExtensionsSettings(installs),
          },
        };
      },
      providesTags: [ApiTags.apps],
    }),
    getAppInstallEmbedCode: build.query<
      string,
      {
        appId: string;
        userId?: string;
        companyId?: string;
        notificationId?: string;
      }
    >({
      query: ({ appId, userId, companyId, notificationId }) => ({
        method: ApiMethods.get,
        path: `/v1/app-installs/${appId}/embed`,
        options: {
          queryStringParameters: {
            userId,
            companyId,
            notificationId,
          },
        },
      }),
    }),
    getWebhookEvents: build.query<MarketplaceApplicationWebhook[], void>({
      query: () => ({
        path: '/apps/webhook-events',
        method: ApiMethods.get,
        options: {
          queryStringParameters: {},
        },
      }),
    }),
  }),
});

export const {
  useCreateAppMutation,
  useGetAppQuery,
  useUpdateAppMutation,
  useDeleteAppMutation,
  useGetInstallsQuery,
  useGetAppInstallEmbedCodeQuery,
  useGetWebhookEventsQuery,
} = applicationSettingsApi;
